import { useEffect, useRef, useState, FunctionComponent } from "react";
import { useOnceRef } from "./once-ref";
import {
	createAtom,
	createComputedAtom,
	createReaction,
	untrack,
} from "fluidstate";

let reactiveObjectId = 1;

const COMPUTED_CONFIG = {
	equals: () => false,
};

/**
 * A Higher-Order Component (HOC) that makes a React component reactive.
 * The wrapped component will automatically re-render whenever any `fluidstate`
 * reactive properties accessed during its render cycle change.
 *
 * @template P The props type of the component to wrap.
 * @param {FunctionComponent<P>} Component The React functional component to make reactive.
 * @returns {FunctionComponent<P>} A new reactive component that wraps the original one.
 */
export const withReactive = <P>(
	Component: FunctionComponent<P>
): FunctionComponent<P> => {
	const FluidComponent: FunctionComponent<P> = (...args) => {
		const argsRef = useRef(args);
		argsRef.current = args;

		const data = useOnceRef<{
			isInitial: boolean;
			isWithinRender: boolean;
			shouldRerender: boolean;
			hasError: boolean;
			error: unknown;
		}>(() => ({
			isInitial: true,
			isWithinRender: false,
			shouldRerender: false,
			hasError: false,
			error: null,
		}));

		const [, rerender] = useState(Symbol());
		const renderControl = useOnceRef(() =>
			createAtom(
				`withReactive(${
					Component.displayName || Component.name || "Unknown"
				})/renderControl@${reactiveObjectId}`
			)
		);

		// We should only rerun component render within rendering context
		// and in a way that preserves hook order
		const computed = useOnceRef(() =>
			createComputedAtom(
				`withReactive(${
					Component.displayName || Component.name || "Unknown"
				})/computed@${reactiveObjectId}`,
				() => {
					renderControl.current.reportObserved();

					if (data.current.isWithinRender) {
						try {
							data.current.hasError = false;
							data.current.error = null;
							return Component(...argsRef.current);
						} catch (e) {
							// Note: throwing inside computed causes issues with
							//       some reactive layers (e.g. in preact layer
							//       it causes out-of-order effect)
							data.current.hasError = true;
							data.current.error = e;
						}
					} else {
						data.current.shouldRerender = true;
					}

					return null;
				},
				COMPUTED_CONFIG
			)
		);

		const reaction = useOnceRef(() =>
			createReaction(() => {
				computed.current.get();
				if (data.current.isInitial) {
					return;
				}
				if (data.current.shouldRerender) {
					data.current.shouldRerender = false;
					untrack(() => {
						rerender(Symbol());
					});
				}
			})
		);

		useEffect(() => {
			return () => {
				reaction.current.stop();
			};
		}, []);

		data.current.isWithinRender = true;
		renderControl.current.reportChanged();
		try {
			const result = computed.current.get();
			if (data.current.hasError) {
				throw data.current.error;
			}
			return result;
		} finally {
			data.current.isInitial = false;
			data.current.shouldRerender = false;
			data.current.isWithinRender = false;
		}
	};

	if (Component.displayName || Component.name) {
		FluidComponent.displayName = `FluidComponent(${
			Component.displayName || Component.name
		})`;
	}

	return FluidComponent;
};
